import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError, get } from "@/lib/api";
import { PageHeader, Section } from "@/components/common/Layout";
import { StatusBadge, ScoreBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { dt, relative, title } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { SendToCrmDialog } from "@/components/crm/SendToCrmDialog";

const STAGES = ["new", "to_review", "assigned", "contact_attempted", "callback_scheduled",
  "in_conversation", "qualified", "offer_sent", "converted", "lost"];
const LEVELS = ["hot", "warm", "cold", "unqualified", "unknown"];

const Field = ({ label, value, testId }) => (
  <div className="rounded-md border border-slate-200 px-3 py-2" data-testid={testId}>
    <p className="kpi-label">{label}</p>
    <p className="text-sm font-medium text-slate-800">{value ?? "—"}</p>
  </div>
);

export default function LeadDetail() {
  const { id } = useParams();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [note, setNote] = useState("");
  const [form, setForm] = useState({});
  const [crmOpen, setCrmOpen] = useState(false);
  const { data: lead, isLoading } = useQuery({
    queryKey: ["lead", id],
    queryFn: () => get(`/leads/${id}`),
    refetchInterval: 20000,
  });
  const users = useQuery({ queryKey: ["assignable"], queryFn: () => get("/users/assignable") });
  const categories = useQuery({
    queryKey: ["lead-categories"],
    queryFn: () => get("/classification/config"),
  });

  const update = useMutation({
    mutationFn: (payload) => api.patch(`/leads/${id}`, payload),
    onSuccess: () => {
      toast.success("Lead updated");
      setNote("");
      setForm({});
      queryClient.invalidateQueries({ queryKey: ["lead", id] });
      queryClient.invalidateQueries({ queryKey: ["leads-board"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail?.message || apiError(e)),
  });

  if (isLoading || !lead) {
    return <PageHeader testId="lead-detail-loading" title="Lead" subtitle="Loading…" />;
  }
  const writable = can("leads", "write");

  return (
    <>
      <PageHeader
        testId="lead-detail-page"
        title={lead.contact_name || "Lead"}
        subtitle={`${lead.company || lead.phone} · ${lead.product || "no product"} · created ${dt(lead.created_at)}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge value="running" label={title(lead.pipeline_stage || "new")} testId="lead-detail-stage" />
            <ScoreBadge score={lead.interest_score} />
            {writable && lead.pipeline_stage === "qualified" ? (
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => setCrmOpen(true)} data-testid="lead-send-crm">
                Send to CRM
              </Button>
            ) : null}
            <Button variant="outline" size="sm" asChild data-testid="lead-back">
              <Link to="/leads">Back to pipeline</Link>
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-6">
        <Field label="Interest level" value={title(lead.interest_level || "unknown")} testId="lead-interest-level" />
        <Field label="Lead category" value={lead.lead_category ? title(lead.lead_category) : "—"} testId="lead-category" />
        <Field label="Assigned user" value={lead.assigned_to_name || "Unassigned"} testId="lead-assignee" />
        <Field label="Next action" value={lead.next_action} testId="lead-next-action" />
        <Field label="Callback" value={lead.preferred_callback_at ? dt(lead.preferred_callback_at) : "—"} testId="lead-callback" />
        <Field label="Call attempts" value={lead.call_attempts || (lead.calls || []).length} testId="lead-attempts" />
        <Field label="Source campaign" value={lead.campaign_name} testId="lead-campaign" />
        <Field label="Source lists" value={(lead.source_lists || []).map((l) => l.name).join(", ") || "—"} testId="lead-source-lists" />
        <Field label="Country / language" value={`${(lead.country || "").toUpperCase()} / ${(lead.language || "").toUpperCase()}`} testId="lead-locale" />
        <Field label="Final outcome" value={lead.final_outcome ? title(lead.final_outcome) : "—"} testId="lead-final-outcome" />
        <Field label="Last activity" value={relative(lead.last_activity_at || lead.updated_at)} testId="lead-last-activity" />
        <Field label="Exports" value={(lead.exports || []).length} testId="lead-export-count" />
        <Field label="CRM" value={lead.crm_sync?.status ? title(lead.crm_sync.status) : "Not sent"} testId="lead-crm-status" />
        <Field label="CRM assignee" value={lead.crm_sync?.assignee_label || "—"} testId="lead-crm-assignee" />
        <Field label="CRM sent" value={lead.crm_sync?.sent_at ? dt(lead.crm_sync.sent_at) : "—"} testId="lead-crm-sent" />
      </div>

      {lead.crm_sync ? (
        <Section testId="lead-crm-section" title="CRM sync" subtitle={lead.crm_sync.integration_name || "Last attempt"}>
          <div className="space-y-1 p-4 text-sm text-slate-700">
            <p data-testid="lead-crm-detail">
              {title(lead.crm_sync.status)}
              {lead.crm_sync.http_status ? ` · HTTP ${lead.crm_sync.http_status}` : ""}
              {lead.crm_sync.external_id ? ` · CRM id ${lead.crm_sync.external_id}` : ""}
            </p>
            {lead.crm_sync.error ? <p className="text-rose-700" data-testid="lead-crm-error">{lead.crm_sync.error}</p> : null}
          </div>
        </Section>
      ) : null}

      {writable ? (
        <Section testId="lead-edit-section" title="Update lead">
          <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-3 xl:grid-cols-6">
            <div className="space-y-1.5">
              <Label>Pipeline stage</Label>
              <Select value={form.pipeline_stage || lead.pipeline_stage} onValueChange={(v) => setForm((f) => ({ ...f, pipeline_stage: v }))}>
                <SelectTrigger data-testid="lead-stage-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {STAGES.map((s) => <SelectItem key={s} value={s}>{title(s)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Interest level</Label>
              <Select value={form.interest_level || lead.interest_level || "unknown"} onValueChange={(v) => setForm((f) => ({ ...f, interest_level: v }))}>
                <SelectTrigger data-testid="lead-interest-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {LEVELS.map((s) => <SelectItem key={s} value={s}>{title(s)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Lead category</Label>
              <Select value={form.lead_category || lead.lead_category || "none"} onValueChange={(v) => setForm((f) => ({ ...f, lead_category: v === "none" ? null : v }))}>
                <SelectTrigger data-testid="lead-category-select"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="none">None</SelectItem>
                  {(categories.data?.lead_categories || []).map((c) => <SelectItem key={c.key} value={c.key}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Assigned user</Label>
              <Select value={form.assigned_to ?? (lead.assigned_to || "none")} onValueChange={(v) => setForm((f) => ({ ...f, assigned_to: v === "none" ? "" : v }))}>
                <SelectTrigger data-testid="lead-assign-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="none">Unassigned</SelectItem>
                  {(users.data || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Next action</Label>
              <Input data-testid="lead-next-action-input" value={form.next_action ?? (lead.next_action || "")} onChange={(e) => setForm((f) => ({ ...f, next_action: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Callback date</Label>
              <Input data-testid="lead-callback-input" type="datetime-local" value={(form.preferred_callback_at ?? lead.preferred_callback_at ?? "").slice(0, 16)} onChange={(e) => setForm((f) => ({ ...f, preferred_callback_at: e.target.value ? new Date(e.target.value).toISOString() : null }))} />
            </div>
            <div className="space-y-1.5 md:col-span-2 xl:col-span-4">
              <Label>Internal note</Label>
              <Textarea rows={2} data-testid="lead-note-input" value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <div className="flex items-end gap-2 md:col-span-1 xl:col-span-2">
              <Button className="bg-blue-600 hover:bg-blue-700" disabled={update.isPending}
                onClick={() => update.mutate({ ...form, assigned_to: form.assigned_to === "" ? null : form.assigned_to, note: note || undefined })}
                data-testid="lead-save">
                Save changes
              </Button>
              <Button variant="outline" onClick={() => update.mutate({ pipeline_stage: "converted" })} data-testid="lead-mark-converted">Mark converted</Button>
              <Button variant="outline" className="text-rose-600" onClick={() => update.mutate({ pipeline_stage: "lost" })} data-testid="lead-mark-lost">Mark lost</Button>
            </div>
          </div>
        </Section>
      ) : null}

      <Tabs defaultValue="ai">
        <TabsList className="flex-wrap">
          <TabsTrigger value="ai" data-testid="lead-tab-ai">AI insight</TabsTrigger>
          <TabsTrigger value="calls" data-testid="lead-tab-calls">Calls &amp; transcripts</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="lead-tab-timeline">Activity timeline</TabsTrigger>
          <TabsTrigger value="history" data-testid="lead-tab-history">Stage &amp; classification history</TabsTrigger>
          <TabsTrigger value="exports" data-testid="lead-tab-exports">Exports &amp; audit</TabsTrigger>
        </TabsList>

        <TabsContent value="ai" className="mt-4">
          <Section testId="lead-ai-section" title="AI summary">
            <div className="space-y-3 p-4">
              <p className="text-sm text-slate-700" data-testid="lead-summary">{lead.summary || "No AI summary recorded."}</p>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="rounded-md border border-slate-200 p-3" data-testid="lead-objections">
                  <p className="kpi-label mb-1">Objections</p>
                  {(lead.objections || []).length ? lead.objections.map((o, i) => (
                    <p key={i} className="text-xs text-slate-600">• {title(String(o))}</p>
                  )) : <p className="text-xs text-slate-400">None recorded</p>}
                </div>
                <div className="rounded-md border border-slate-200 p-3" data-testid="lead-questions">
                  <p className="kpi-label mb-1">Customer questions</p>
                  {(lead.questions || []).length ? lead.questions.map((q, i) => (
                    <p key={i} className="text-xs text-slate-600">• {q}</p>
                  )) : <p className="text-xs text-slate-400">None recorded</p>}
                </div>
              </div>
              <div className="rounded-md border border-slate-200 p-3" data-testid="lead-notes">
                <p className="kpi-label mb-1">Internal notes</p>
                {(lead.notes || []).length ? lead.notes.map((n) => (
                  <p key={n.id} className="text-xs text-slate-600">• {n.text} <span className="text-slate-400">({n.author}, {dt(n.created_at)})</span></p>
                )) : <p className="text-xs text-slate-400">No notes yet</p>}
              </div>
            </div>
          </Section>
        </TabsContent>

        <TabsContent value="calls" className="mt-4">
          <Section testId="lead-calls-section" title={`${(lead.calls || []).length} related calls`}>
            <div className="space-y-2 p-4">
              {(lead.calls || []).map((call) => (
                <div key={call.id} className="rounded-md border border-slate-200 p-3" data-testid={`lead-call-${call.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm font-medium text-slate-800">{dt(call.created_at)} · {title(call.outcome || call.status)}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">{call.duration_seconds || 0}s · {call.language}</span>
                      <Button size="sm" variant="ghost" asChild data-testid={`lead-open-call-${call.id}`}>
                        <Link to={`/calls/${call.id}`}>Open call</Link>
                      </Button>
                    </div>
                  </div>
                  {call.recording_url ? (
                    <audio controls src={call.recording_url} className="mt-2 w-full" data-testid={`lead-recording-${call.id}`} />
                  ) : null}
                </div>
              ))}
              {(lead.transcript || []).length ? (
                <div className="rounded-md border border-slate-200 p-3" data-testid="lead-transcript">
                  <p className="kpi-label mb-1">
                    Latest transcript
                    {lead.transcript_source ? (
                      <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400" data-testid="lead-transcript-source">
                        source: {lead.transcript_source}
                      </span>
                    ) : null}
                  </p>
                  {lead.transcript.map((line, i) => (
                    <p key={i} className="text-xs text-slate-600">
                      <span className="font-medium text-slate-700">{title(line.speaker)}:</span> {line.text}
                    </p>
                  ))}
                </div>
              ) : null}
            </div>
          </Section>
        </TabsContent>

        <TabsContent value="timeline" className="mt-4">
          <Section testId="lead-timeline-section" title="Activity timeline">
            <div className="space-y-2 p-4">
              {(lead.activities || []).length ? lead.activities.map((a) => (
                <div key={a.id} className="flex items-start justify-between gap-3 border-b border-slate-100 pb-2 text-xs" data-testid={`activity-${a.id}`}>
                  <div>
                    <p className="font-medium text-slate-700">{title(a.event)}</p>
                    <p className="text-slate-500">{JSON.stringify(a.data)}</p>
                  </div>
                  <span className="whitespace-nowrap text-slate-400">{a.actor} · {dt(a.created_at)}</span>
                </div>
              )) : <p className="text-xs text-slate-400">No activity recorded yet</p>}
            </div>
          </Section>
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Section testId="lead-stage-history" title="Stage history">
              <div className="space-y-1 p-4 text-xs">
                {(lead.history || []).length ? lead.history.map((h) => (
                  <p key={h.id} className="text-slate-600">
                    {title(h.from_status || "none")} → <span className="font-medium">{title(h.to_status)}</span>{" "}
                    <span className="text-slate-400">by {h.changed_by} · {dt(h.created_at)}</span>
                  </p>
                )) : <p className="text-slate-400">No stage changes yet</p>}
              </div>
            </Section>
            <Section testId="lead-classification-history" title="Manual classification history">
              <div className="space-y-1 p-4 text-xs">
                {(lead.classification_corrections || []).length ? lead.classification_corrections.map((c) => (
                  <p key={c.id} className="text-slate-600">
                    {JSON.stringify(c.old_value)} → {JSON.stringify(c.new_value)}{" "}
                    <span className="text-slate-400">by {c.corrected_by} · {dt(c.created_at)}</span>
                  </p>
                )) : <p className="text-slate-400">No manual corrections</p>}
              </div>
            </Section>
          </div>
        </TabsContent>

        <TabsContent value="exports" className="mt-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Section testId="lead-exports-section" title="Export history">
              <div className="space-y-1 p-4 text-xs">
                {(lead.exports || []).length ? lead.exports.map((e) => (
                  <p key={e.batch_id + e.created_at} className="text-slate-600" data-testid={`lead-export-${e.batch_id}`}>
                    Batch {e.batch_id.slice(0, 8)} · {dt(e.created_at)}
                  </p>
                )) : <p className="text-slate-400">This lead has never been exported</p>}
              </div>
            </Section>
            <Section testId="lead-audit-section" title="Audit history">
              <div className="space-y-1 p-4 text-xs">
                {(lead.audit || []).length ? lead.audit.map((a) => (
                  <p key={a.id} className="text-slate-600">
                    {a.action} <span className="text-slate-400">by {a.user_email || "system"} · {dt(a.created_at)}</span>
                  </p>
                )) : <p className="text-slate-400">No audit entries</p>}
              </div>
            </Section>
          </div>
        </TabsContent>
      </Tabs>

      <SendToCrmDialog
        open={crmOpen}
        onClose={() => setCrmOpen(false)}
        leadIds={[lead.id]}
        onSent={() => queryClient.invalidateQueries({ queryKey: ["lead", id] })}
      />
    </>
  );
}